import type { JSX, ReactNode } from 'react';
import { forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { Button } from '../Button';
import type { ButtonAsButtonProps, ButtonAsLinkProps } from '../Button/Button';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import type { IconNameType } from '../Icon';
import { Icon } from '../Icon';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';

type StyledActionButtonProps = {
  $colorScheme: ColorSchemeTypes;
  $hoverColorScheme: ColorSchemeTypes;
};

const StyledActionButton = styled(Button)<StyledActionButtonProps>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  display: grid;
  grid-template-columns: 24px 1fr;
  grid-template-rows: 24px auto;
  gap: var(--wui-space-02);
  width: fit-content;
  min-width: 96px;
  padding: var(--wui-space-03);
  background: var(--wui-color-bg-surface-secondary);
  row-gap: var(--wui-space-02);
  border-radius: var(--wui-border-radius-03);
  flex: 0 0 auto;
  transition: background-color var(--wui-motion-duration-02) var(--wui-motion-ease);

  &:focus {
    outline: none;
  }

  &:hover,
  &:focus-visible,
  &.force-state-hover,
  &.force-state-focus {
    --wui-color-text: var(--wui-color-text-button);

    ${({ $hoverColorScheme }) => getColorScheme($hoverColorScheme)};
    background: var(--wui-color-bg-surface-tertiary);
  }

  &:focus-visible,
  &.force-state-focus {
    --wui-action-button-focus-color: var(--wui-color-focus-ring);

    box-shadow:
      inset 0 0 0 2px var(--wui-action-button-focus-color),
      inset 0 0 0 3px var(--wui-color-bg-app);
  }

  &[aria-disabled='true'] {
    --wui-action-button-focus-color: var(--wui-color-focus-ring-disabled);

    background: var(--wui-color-bg-surface-disabled);
    color: var(--wui-color-text-disabled);
    cursor: not-allowed;

    && {
      svg {
        color: var(--wui-color-icon-disabled);
      }
    }
  }
`;

const StyledMainIcon = styled.div`
  grid-column: 1;
  grid-row: 1;
  width: 24px;
  height: 24px;
  transition: color var(--wui-motion-duration-02) var(--wui-motion-ease);

  && {
    svg {
      width: 24px;
      height: 24px;
      flex: 0 0 24px;
    }
  }
`;

type StyledSecondaryIconProps = {
  $hideUntilHover: boolean;
};

const StyledSecondaryIcon = styled.div<StyledSecondaryIconProps>`
  grid-column: 2;
  grid-row: 1;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  transition: all var(--wui-motion-duration-02) var(--wui-motion-ease-out);

  ${({ $hideUntilHover }) =>
    $hideUntilHover &&
    css`
      will-change: transform;
      opacity: 0;
      transform: translateX(-16px);
      ${StyledActionButton}:hover:not([aria-disabled="true"]) &,
      ${StyledActionButton}:focus-visible:not([aria-disabled="true"]) &,
      ${StyledActionButton}.force-state-hover:not([aria-disabled="true"]) &,
      ${StyledActionButton}.force-state-focus:not([aria-disabled="true"]) & {
        transform: translateX(0);
        opacity: 1;
      }
    `};

  && {
    svg {
      width: 24px;
      height: 24px;
      flex: 0 0 24px;
    }
  }
`;

type StyledLabelProps = {
  $disabled?: boolean;
};

const StyledLabel = styled.span<StyledLabelProps>`
  font-family: var(--wui-typography-heading-4-family);
  font-size: var(--wui-typography-heading-4-size);
  font-weight: var(--wui-typography-heading-4-weight);
  line-height: var(--wui-typography-heading-4-line-height);
  color: ${({ $disabled }) =>
    $disabled ? 'var(--wui-color-text-disabled)' : 'var(--wui-color-text)'};
  grid-column: 1 / -1;
  grid-row: 2;
  text-align: left;
`;

export type ActionButtonProps = {
  /**
   * @override
   * The text to display for the ActionButton
   *
   */
  children: ReactNode;
  /**
   * The [Icon](?path=/docs/components-icon--docs) component to use, e.g. `<Icon type="favorite" />`
   */
  icon: JSX.Element;
  /**
   * @override
   * Disables the button
   */
  disabled?: boolean;
  /**
   * @ignore
   * Force a button into a particular css state (for debugging)
   */
  forceState?: 'focus' | 'hover' | undefined;
  /**
   * Set the [color scheme](../?path=/docs/docs-color-schemes--docs) of the `ActionButton` when hovered. By default, it will inherit the `colorScheme` prop.
   */
  hoverColorScheme?: ColorSchemeTypes | undefined;
  /**
   * Changes the secondary icon for different use cases
   */
  variant?: 'default' | 'gated' | 'menu-down' | 'menu-up';
} & (
  | Omit<
      ButtonAsButtonProps,
      'fullWidth' | 'isLoading' | 'leftIcon' | 'rightIcon' | 'size' | 'unstyled' | 'variant'
    >
  | Omit<
      ButtonAsLinkProps,
      'fullWidth' | 'isLoading' | 'leftIcon' | 'rightIcon' | 'size' | 'unstyled' | 'variant'
    >
);

/**
 * Action Button component is used as one of a group of main actions on the page. It composes [Button]().
 */
export const ActionButton = forwardRef<HTMLAnchorElement | HTMLButtonElement, ActionButtonProps>(
  (
    {
      icon,
      colorScheme = 'standard',
      disabled = false,
      forceState,
      hoverColorScheme,
      variant = 'default',
      children,
      ...props
    }: ActionButtonProps,
    ref,
  ) => {
    const resolvedHoverColorScheme =
      hoverColorScheme ?? (variant === 'gated' ? 'purple' : 'inherit'); // Use purple hoverColorScheme for gated state unless explictly set

    const secondaryIconMap: Record<NonNullable<ActionButtonProps['variant']>, IconNameType> = {
      default: 'arrow-right',
      gated: 'sparkle',
      'menu-up': 'caret-up',
      'menu-down': 'caret-down',
    };

    return (
      <StyledActionButton
        {...props}
        ref={ref}
        $colorScheme={colorScheme}
        $hoverColorScheme={resolvedHoverColorScheme}
        disabled={disabled}
        forceState={forceState}
        unstyled={true}
      >
        <StyledMainIcon>{icon}</StyledMainIcon>
        <StyledSecondaryIcon $hideUntilHover={variant === 'default'}>
          <Icon
            colorScheme={variant === 'gated' ? 'purple' : 'inherit'}
            type={secondaryIconMap[variant]}
          />
        </StyledSecondaryIcon>
        <StyledLabel $disabled={disabled}>{children}</StyledLabel>
      </StyledActionButton>
    );
  },
);

ActionButton.displayName = 'ActionButton';
